import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import AlertComponent from "../_components/AlertComponent";
import LoadingSpinnerComponent from "../_components/LoadingSpinnerComponent";
import { billingActions } from "../_actions";
import MarkdownIt from "markdown-it";
import { getBool, toGermanDateString } from "../helper/formatting-helper";
import { determineCurrentLocale } from "../_helpers/LocaleHelper";
import { useLocation, useParams } from "react-router";
import { prepareTemplateCreditNote } from "../_helpers/mocking-helper";
import {
    AdditionalInformationLines,
    AddressBox,
    Footer,
    Logo,
    OpenFiskalLogo,
} from "../InvoiceTemplate/InvoiceComponents";
import {
    BasicCreditNoteInformationBox,
    CreditNoteLineItems,
    CreditNoteNumber,
    CreditNoteOrderAdjustments,
    CreditNoteTotalBox,
    CreditNoteTransactions,
} from "./CreditNoteComponents";
import useColors from "../helper/useColors";
import stringUtils from "../_helpers/string-utils";

const markdownIt = new MarkdownIt(/* Markdown-it options */);

const CreditNoteTemplate = () => {
    const dispatch = useDispatch();
    const billing = useSelector(state => state.billing);
    const location = useLocation();
    const { id, refundId } = useParams();

    const params = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    const configurationId = useMemo(() => {
        return params.has("configurationId") ? params.get("configurationId") : null;
    }, [params]);

    const creditNoteDate = useMemo(() => {
        return params.has("creditNoteDate") ? params.get("creditNoteDate") : toGermanDateString(new Date());
    }, [params]);

    const creditNoteNumberParam = useMemo(() => {
        return params.has("creditNoteNumber") ? params.get("creditNoteNumber") : null;
    }, [params]);

    const isDummy = useMemo(() => {
        return params.has("dummy") ? getBool(params.get("dummy")) : false;
    }, [params]);

    const isReversalDocument = useMemo(() => {
        return params.has("reversal") ? getBool(params.get("reversal")) : false;
    }, [params]);


    useEffect(() => {
        if (id) {
            dispatch(billingActions.getCreditNoteData(id, refundId));
        }
    }, [dispatch, id, refundId]);

    useEffect(() => {
        if (configurationId) {
            dispatch(
                billingActions.getDocumentSettings(
                    configurationId,
                    id,
                    "CREDIT_NOTE",
                    refundId,
                ),
            );
        }
    }, [configurationId, id, refundId, dispatch]);

    const creditNoteData = useMemo(() => {
        if (billing.orderInvoicingConfiguration && billing.orderInvoicingConfiguration.externalUUID) {
            let ft = params.has("footerText") ? params.get("footerText") : billing.orderInvoicingConfiguration.footerText;
            let result = ft ? markdownIt.render(ft || "") : null;

            return {
                creditNoteNumber: creditNoteNumberParam || ((billing.orderInvoicingConfiguration.creditNoteNumberPrefix || "") +
                    billing.orderInvoicingConfiguration.nextCreditNoteNumber +
                    (billing.orderInvoicingConfiguration.creditNoteNumberSuffix || "")),
                invoicingConfiguration: {
                    ...billing.orderInvoicingConfiguration,
                    logoAlignment: params.has("logoAlignment")
                        ? params.get("logoAlignment")
                        : billing.orderInvoicingConfiguration.logoAlignment,
                    secondaryColor: params.has("secondaryColor")
                        ? params.get("secondaryColor")
                        : billing.orderInvoicingConfiguration.secondaryColor,
                    primaryColor: params.has("primaryColor")
                        ? params.get("primaryColor")
                        : billing.orderInvoicingConfiguration.primaryColor,
                    footerText: result,
                    showLogo: params.has("showLogo") ? params.get("showLogo") : billing.orderInvoicingConfiguration.showLogo,
                    uploadedLogoFile: params.has("logoUUID")
                        ? params.get("logoUUID")
                        : billing.orderInvoicingConfiguration.uploadedLogoFile,
                },
                creditNote: billing.creditNote
                    ? billing.creditNote
                    : isDummy
                        ? prepareTemplateCreditNote(billing.orderInvoicingConfiguration)
                        : null,
            };
        } else {
            return {
                creditNoteNumber: "CN-1234",
                creditNote: prepareTemplateCreditNote(null),
                invoicingConfiguration: {
                    showLogo: params.has("showLogo") ? params.get("showLogo") : null,
                    logoAlignment: params.has("logoAlignment") ? params.get("logoAlignment") : "LEFT",
                    secondaryColor: params.has("secondaryColor") ? params.get("secondaryColor") : "000000",
                    primaryColor: params.has("primaryColor") ? params.get("primaryColor") : "000000",
                    footerText: params.has("footerText")
                        ? params.get("footerText")
                        : "Beispieltext in der Fußzeile.",
                    uploadedLogoFile: params.has("logoUUID")
                        ? params.get("logoUUID")
                        : process.env.REACT_APP_S3_PUBLIC_BUCKET + "/logos/placeholder.png",
                },
            };
        }
    }, [params, billing.orderInvoicingConfiguration, billing.creditNote, creditNoteNumberParam, isDummy]);

    const currentLocale = useMemo(() => {
        return determineCurrentLocale(billing.orderInvoicingConfiguration ? billing.orderInvoicingConfiguration.language : undefined);
    }, [billing.orderInvoicingConfiguration]);

    const hasDiscount = useMemo(() => {
        return creditNoteData.creditNote &&
            creditNoteData.creditNote.lineItems &&
            creditNoteData.creditNote.lineItems.filter((item) => item.discounts && item.discounts.length > 0).length > 0;
    }, [creditNoteData]);

    const { primaryColor, secondaryColor } = useColors(creditNoteData.invoicingConfiguration);

    if (billing.loading || billing.invoicingConfigurationLoading) {
        return (
            <div className="container">
                <Row>
                    <Col>
                        <br />
                        <br />
                        <br />
                        <br />
                        <br />
                        <br />
                        <br />
                        <LoadingSpinnerComponent loading={true} size="6em" center={true} />
                    </Col>
                </Row>
            </div>
        );
    } else if (!creditNoteData.creditNote && !billing.errorMessage) {
        return (
            <div className="container">
                <Row>
                    <Col>
                        <br />
                        <br />
                        <br />
                        <AlertComponent errorMessages={["Es gab ein Problem beim Laden der Gutschrift"]} />
                    </Col>
                </Row>
            </div>
        );
    } else if (billing.errorMessage) {
        return (
            <div className="container">
                <Row>
                    <Col>
                        <br />
                        <br />
                        <br />
                        <AlertComponent errorMessages={billing.errorMessage} />
                    </Col>
                </Row>
            </div>
        );
    } else if (!creditNoteData.invoicingConfiguration) {
        return (
            <div className="container">
                <Row>
                    <Col>
                        <AlertComponent errorMessages={["Es gab ein Problem beim Laden der Einstellungen"]} />
                    </Col>
                </Row>
            </div>
        );
    } else {
        return (
            <div className="document-container">
                <div id="main-box">
                    <Logo logo={creditNoteData.invoicingConfiguration.uploadedLogoFile}
                          logoAlignment={creditNoteData.invoicingConfiguration.logoAlignment.toLowerCase()}
                          hidden={!creditNoteData.invoicingConfiguration.showLogo} />

                    <CreditNoteNumber creditNoteNumber={creditNoteData.creditNoteNumber}
                                      isDummy={isDummy}
                                      testMode={creditNoteData.invoicingConfiguration.testMode}
                                      isReversalDocument={isReversalDocument}
                                      color={primaryColor}
                                      locale={currentLocale} />

                    <br />

                    <AddressBox document={creditNoteData.creditNote}
                                displayEmailAddresses={creditNoteData.invoicingConfiguration.displayEmailAddresses}
                                leftAlignedAddress={creditNoteData.invoicingConfiguration.leftAlignedAddress}
                                color={primaryColor}
                                locale={currentLocale} />

                    <hr />

                    <BasicCreditNoteInformationBox creditNote={creditNoteData.creditNote}
                                                   creditNoteDate={creditNoteDate}
                                                   showCreditNoteText={creditNoteData.invoicingConfiguration.showCreditNoteText}
                                                   locale={currentLocale} />

                    <hr />
                    <br />

                    <CreditNoteLineItems lineItems={creditNoteData.creditNote.lineItems}
                                         additionalInformationLineItems={creditNoteData.invoicingConfiguration.additionalInformationLines}
                                         currencyCode={creditNoteData.creditNote.currencyCode}
                                         hasDiscount={hasDiscount}
                                         color={secondaryColor}
                                         locale={currentLocale} />

                    {creditNoteData.creditNote.lineItems &&
                        creditNoteData.creditNote.lineItems.length > 0 &&
                        creditNoteData.creditNote.orderAdjustments &&
                        creditNoteData.creditNote.orderAdjustments.length > 0 && (
                            <>
                                <br />
                            </>
                        )}

                    <CreditNoteOrderAdjustments orderAdjustments={creditNoteData.creditNote.orderAdjustments}
                                                currencyCode={creditNoteData.creditNote.currencyCode}
                                                color={secondaryColor}
                                                locale={currentLocale} />

                    <CreditNoteTotalBox creditNote={creditNoteData.creditNote}
                                        color={secondaryColor}
                                        locale={currentLocale} />

                    <CreditNoteTransactions transactions={creditNoteData.creditNote.transactions}
                                            locale={currentLocale} />

                    {creditNoteData.invoicingConfiguration.additionalInformationLines &&
                        creditNoteData.invoicingConfiguration.additionalInformationLines.filter(line => stringUtils.isBlank(line.linkedLineItemId)).length > 0 && (
                            <AdditionalInformationLines
                                additionalInformationLines={creditNoteData.invoicingConfiguration.additionalInformationLines} />
                        )}

                    {creditNoteData.invoicingConfiguration.ofiKey &&
                        <>
                            <br />
                            <OpenFiskalLogo shopId={creditNoteData.creditNote.shopId}
                                            orderId={creditNoteData.creditNote.orderId}
                                            ofiKey={creditNoteData.invoicingConfiguration.ofiKey} />
                        </>
                    }
                </div>

                {creditNoteData.invoicingConfiguration.footerText && creditNoteData.invoicingConfiguration.footerText !== "" && (
                    <div id="footer-box">
                        <Footer text={creditNoteData.invoicingConfiguration.footerText} />
                    </div>
                )}
            </div>
        );
    }
};

export default CreditNoteTemplate;